import React, { useState, useCallback, useMemo } from 'react';
import { formatPhoneDisplay } from '../../../utils/phoneUtils';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import VideocamIcon from '@mui/icons-material/Videocam';
import type { WebsiteCrawlResult, ProviderInfo, RoleOption } from '../types';
import { InfoRow, clinicTypeLabel } from './crawl/InfoRows';
import { normaliseStaffFromCrawl } from '../utils/crawlRoleMapper';
import { crawlStaffArrayToProviders, providerInfoToCrawlStaff } from '../utils/crawlToProvider';
import UnifiedProviderCard from './provider/UnifiedProviderCard';
import { PAY_TYPE_OPTIONS, TIMESHEET_MODE_OPTIONS } from './provider/providerCardConstants';

/** Derive the modality label — prefer explicit modality field, fall back to inference. */
function modalityLabel(data: WebsiteCrawlResult): string | null {
  if (data.modality === 'both') return 'Both (In-Person & Telehealth)';
  if (data.modality === 'telehealth') return 'Telehealth Only';
  if (data.modality === 'in_person') return 'In-Person Only';
  const hasAddress = Boolean(data.address);
  const hasTelehealth = data.telehealth_offered === true;
  if (hasAddress && hasTelehealth) return 'Both (In-Person & Telehealth)';
  if (hasTelehealth) return 'Telehealth Only';
  if (hasAddress) return 'In-Person Only';
  return null;
}

interface WebsiteCrawlConfirmationProps {
  open: boolean;
  data: WebsiteCrawlResult;
  onConfirm: (editedData: WebsiteCrawlResult) => void;
  onSkip: () => void;
  loading?: boolean;
  roleOptions: RoleOption[];
  licenseTypes: string[];
  degreeTypes: string[];
}

/**
 * Confirmation dialog showing data extracted from a clinic website crawl.
 *
 * Uses UnifiedProviderCard for inline editing of each staff member.
 * Sections with crawl-found data auto-expand with blue highlights;
 * empty sections are collapsed but expandable.
 * "Looks great — confirm" now preserves ALL inline edits.
 */
export default function WebsiteCrawlConfirmation({
  open,
  data,
  onConfirm,
  onSkip,
  loading = false,
  roleOptions,
  licenseTypes,
  degreeTypes,
}: WebsiteCrawlConfirmationProps) {
  // Normalise crawl staff → CrawledStaffMember[] → ProviderInfo[]
  const normalisedStaff = useMemo(
    () => normaliseStaffFromCrawl(data.staff ?? [], roleOptions, licenseTypes, degreeTypes, data.specialties ?? []),
    [data.staff, roleOptions, licenseTypes, degreeTypes, data.specialties],
  );
  const normalisedProviders = useMemo(
    () => crawlStaffArrayToProviders(normalisedStaff),
    [normalisedStaff],
  );

  const [localProviders, setLocalProviders] = useState<ProviderInfo[]>(normalisedProviders);
  React.useEffect(() => setLocalProviders(normalisedProviders), [normalisedProviders]);

  const handleProviderUpdate = useCallback((idx: number, field: string, value: unknown) => {
    setLocalProviders((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    );
  }, []);

  const handleProviderRemove = useCallback((idx: number) => {
    setLocalProviders((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const getRoleLabel = useCallback((role: string) => {
    const match = roleOptions.find((o) => o.value === role);
    if (match) return match.label;
    return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }, [roleOptions]);

  // Convert ProviderInfo[] back to CrawledStaffMember[] for the backend payload
  const handleConfirm = useCallback(() => {
    const staff = localProviders.map(providerInfoToCrawlStaff);
    onConfirm({ ...data, staff });
  }, [data, localProviders, onConfirm]);

  const hasStaff = (data.staff ?? []).length > 0;
  const hasSpecialties = (data.specialties ?? []).length > 0;
  const hasColors = (data.brand_colors ?? []).length > 0;
  const hasAnyData =
    data.clinic_name || data.address || data.phone || data.email || hasStaff || hasSpecialties;

  const payTypes = useMemo(() => [...PAY_TYPE_OPTIONS], []);
  const timesheetModes = useMemo(() => [...TIMESHEET_MODE_OPTIONS], []);

  return (
    <Dialog open={open} onClose={onSkip} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5 }}>
        <AutoAwesomeIcon color="primary" />
        {hasAnyData ? 'Your website looks great!' : 'Website Scan Complete'}
        <IconButton
          aria-label="close"
          onClick={onSkip}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!hasAnyData ? (
          <Typography variant="body2" color="text.secondary">
            We weren&apos;t able to extract much from that website. No worries &mdash; you can
            enter your information manually below.
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              We checked out your website &mdash; great work! Here&apos;s what we gathered.
              Edit any field inline, then confirm to auto-fill your setup.
            </Typography>

            {/* ---- Clinic Info Section ---- */}
            <InfoRow label="Clinic Name" value={data.clinic_name} />
            <InfoRow label="Address" value={data.address} />
            <InfoRow label="Phone" value={data.phone ? formatPhoneDisplay(data.phone) : ''} />
            <InfoRow label="Email" value={data.email} />
            <InfoRow label="Description" value={data.description} />
            <InfoRow label="Timezone" value={data.timezone} />
            <InfoRow label="Practice Type" value={clinicTypeLabel(data.clinic_type)} />

            {/* ---- Modality ---- */}
            {modalityLabel(data) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Visit Type
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  {data.address && <BusinessIcon fontSize="small" color="action" />}
                  {data.telehealth_offered && <VideocamIcon fontSize="small" color="success" />}
                  <Typography variant="body2">{modalityLabel(data)}</Typography>
                </Box>
              </Box>
            )}

            {/* ---- Logo ---- */}
            {data.logo_url && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Logo
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Box
                    component="img"
                    src={data.logo_url?.startsWith('http') ? data.logo_url : undefined}
                    alt="Clinic logo"
                    sx={{
                      maxHeight: 48,
                      maxWidth: 160,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 0.5,
                    }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* ---- Brand Colors ---- */}
            {hasColors && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Brand Colors
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {(data.brand_colors ?? []).map((color) => (
                    <Box
                      key={color}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        backgroundColor: color,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                      title={color}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* ---- Specialties ---- */}
            {hasSpecialties && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Specialties &amp; Services
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  {(data.specialties ?? []).map((s) => (
                    <Chip key={s} label={s} size="small" variant="outlined" />
                  ))}
                </Box>
              </>
            )}

            {/* ---- Staff — UnifiedProviderCard for each ---- */}
            {hasStaff && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Staff ({localProviders.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {localProviders.map((provider, idx) => (
                    <UnifiedProviderCard
                      key={idx}
                      provider={provider}
                      idx={idx}
                      dataSource="crawl"
                      roleOptions={roleOptions}
                      licenseTypes={licenseTypes}
                      degreeTypes={degreeTypes}
                      payTypes={payTypes}
                      timesheetModes={timesheetModes}
                      showPayType={false}
                      getRoleLabel={getRoleLabel}
                      onRemove={handleProviderRemove}
                      onUpdate={handleProviderUpdate}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1 }}>
        {hasAnyData && (
          <>
            <Button
              variant="contained"
              onClick={handleConfirm}
              fullWidth
              disabled={loading}
              startIcon={<CheckCircleOutlineIcon />}
            >
              {loading ? 'Applying...' : 'Ready to confirm'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              All edits above are preserved. You can always change anything later.
            </Typography>
          </>
        )}
        <Button onClick={onSkip} color="inherit" sx={{ color: 'text.secondary' }} fullWidth disabled={loading}>
          I&apos;ll enter everything manually
        </Button>
      </DialogActions>
    </Dialog>
  );
}
